import React, { useEffect, useMemo } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import Script from 'next/script';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import { useNextMiApolloClient, getDeployedEnvType } from '@marriott/mi-apollo-client-utils';
import { defineComponentMapping, getAllowedComponentsForNonAuthorMode } from '../import-components';
import { CustomModelClient, canUseDOM, UserSessionProvider } from '@marriott/mi-headless-utils';
import { ApolloProvider } from '@apollo/client';
import path from 'path';
import { MockApolloClient } from '../mocks/apolloClient';
import {
  ErrorHandlerAlert,
  useLocaleStore,
  useAuthStore,
  useMediaQueryStore,
  useHeaderFieldsStore,
  DEFAULT_LANG,
  useDttStore,
  isAllowedForLocale,
} from '@marriott/mi-groups-components';
import { version as globalStylesVersion } from '@marriott/global-styles/package.json';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import '../styles/styles.css';
import { baseVariables } from '@marriott/mi-ui-library';
import { useRouter } from 'next/router';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';
path.resolve('./next.config.js');

import NextApp, { AppContext } from 'next/app';
import { sessionTimeoutManager } from '@marriott/shared/mi-helper-utils';
declare global {
  interface Window {
    jQuery: unknown;
    $: unknown;
    deployedEnvType: string;
    sessionData?: {
      data?: {
        AriesCommon?: {
          memState?: string;
        };
      };
      cacheData?: {
        data?: {
          AriesCommon?: {
            memState?: string;
          };
        };
      };
    };
  }
}

const mockUXL = process.env.NEXT_PUBLIC_API_MOCKING === 'true';

if (mockUXL) {
  require('../mocks');
}

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

const App = function (props) {
  const router = useRouter();
  const MiApolloClient = useNextMiApolloClient([], getDeployedEnvType());

  const { Component, pageProps } = props;
  const {
    model,
    pagePath,
    isAuthorMode,
    currentLocale,
    dttFlags = {},
    isDTT: isDttEnabledFromPreprocessor,
    cookies,
    apolloEnvVars = {},
    serverENV,
    requestId,
    headersData,
    NEXT_GLOBAL_ASSET_PREFIX,
    GOOGLE_MAP_API_KEY,
    featureFlagSessionTimeout,
    sessionTimeoutMs,
    isSwpFromHeader = false,
  } = pageProps;

  const serverEnvVars = serverENV ? JSON.parse(serverENV) : {};
  const locale = currentLocale ? currentLocale.replace('_', '-') : DEFAULT_LANG;

  const modelAllowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  // Wrap with useMemo to avoid recomputing on every render
  const allowedComponents = useMemo(() => {
    if (!model) return undefined;
    if ((modelAllowedComponents?.length ?? 0) > 0 || isAuthorMode) return modelAllowedComponents;
    return getAllowedComponentsForNonAuthorMode(model);
  }, [model, modelAllowedComponents, isAuthorMode]);

  const configureRtlPlugin = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  // Get sessionData from global store
  const isClient = typeof window !== 'undefined';

  // Check if user is logged in - only on client side
  const isLoggedIn = React.useMemo(() => {
    if (!isClient) return false;

    const sessionData = window.sessionData;
    return (
      sessionData?.data?.AriesCommon?.memState === 'authenticated' ||
      sessionData?.cacheData?.data?.AriesCommon?.memState === 'authenticated'
    );
  }, [isClient]);

  useEffect(() => {
    if (
      !isAuthorMode &&
      typeof window !== 'undefined' &&
      featureFlagSessionTimeout['experience_mcom_session_timeout_migration'] &&
      sessionTimeoutMs > 0
    ) {
      console.log('Session Timeout - initializing with timeout:', sessionTimeoutMs);
      sessionTimeoutManager.initialize({
        timeoutMs: sessionTimeoutMs,
        isLoggedIn,
        onTimeout() {
          if (typeof window === 'undefined') return;

          console.log('Session related App specific functionality can be added here');
        },
        onUserActivity() {
          console.log('User activity detected - session timer reset');
        },
      });
    }
  }, [sessionTimeoutMs, isLoggedIn, isAuthorMode, featureFlagSessionTimeout]);

  const enableRtl = pageProps?.model?.rtlEnable === 'true';

  if (!dttFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP]) {
    dttFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP] = isDttEnabledFromPreprocessor;
  }

  defineComponentMapping(
    allowedComponents,
    pagePath,
    isAuthorMode,
    currentLocale,
    dttFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP],
    enableRtl,
    !!(dttFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_SWP_GROUPS] || isSwpFromHeader)
  );

  const envVars = {
    ...apolloEnvVars,
    ...serverEnvVars,
  };

  envVars['GOOGLE_MAP_API_KEY'] = GOOGLE_MAP_API_KEY;

  useDttStore.getState().setDttFlags(dttFlags);
  useDttStore.getState().setIsSwp(!!isSwpFromHeader);

  if (!Object.keys(useClientEnvVarsStore.getState().envVarsObject).length) {
    useClientEnvVarsStore.getState().setEnvVars(envVars);
  }

  if (!useLocaleStore.getState().locale) {
    useLocaleStore.getState().setLocale(locale);
  }

  if (useLocaleStore.getState().rtl !== enableRtl) {
    useLocaleStore.getState().setRtl(enableRtl);
  }

  if (canUseDOM) {
    useMediaQueryStore
      .getState()
      .setIsTabletAndAbove(window.innerWidth >= parseInt(baseVariables.mediaQuery.md.match(/\d+/)?.[0] as string));
  }

  useEffect(() => {
    // TODO: added a temporary fallback to populate UserIdToken as the current env is not setting this cookie.
    if (!cookies.UserIdToken) {
      cookies.UserIdToken = cookies.associateToken || cookies.authStateToken;
    }
    useAuthStore.getState().setAuthCookie(cookies);
    useHeaderFieldsStore.getState().setRequestId(requestId);
    headersData.api && useHeaderFieldsStore.getState().setApiValue(headersData.api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthorMode) {
      let remoteURL: URL | string;
      try {
        remoteURL = new URL(document.body.dataset.remoteUrl || '');
        remoteURL = remoteURL.origin;
      } catch (e) {
        remoteURL = '';
      }
      const assetUrl = `${
        process.env['NEXT_PUBLIC_ASSET_DOMAIN'] || remoteURL || 'https://www.marriott.com'
      }${NEXT_GLOBAL_ASSET_PREFIX}`;

      const { asPath } = router;
      const path = asPath.split('/').filter(Boolean);
      const lastSegment = path[path.length - 1];

      const brandCodeMatch = lastSegment?.match(/brandCode=([^&]+)/);
      const brandCode = brandCodeMatch ? brandCodeMatch[1] : '';

      const globalStyle = document.createElement('link');

      globalStyle.rel = 'stylesheet';
      globalStyle.href =
        assetUrl +
        (enableRtl
          ? `global-styles/${globalStylesVersion}/marriot.global.rtl.css`
          : `global-styles/${globalStylesVersion}/marriot.global.css`);
      document.head.appendChild(globalStyle);

      if (brandCode) {
        const brandLink = document.createElement('link');

        brandLink.rel = 'stylesheet';
        brandLink.href = assetUrl + `brand-framework/brand-config.${brandCode}.css`;

        document.head.appendChild(brandLink);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  Object.defineProperty(configureRtlPlugin, 'name', { value: 'rtlPluginMonorepo' });

  return (
    <ErrorHandlerAlert>
      <UserSessionProvider pageProps={pageProps}>
        <StyleSheetManager stylisPlugins={enableRtl ? [configureRtlPlugin] : []}>
          <ApolloProvider client={mockUXL ? MockApolloClient : MiApolloClient}>
            <Component {...pageProps} />
            {isAllowedForLocale(locale) && (
              <>
                <Script src={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}`} defer={true} />
                <Script src="https://unpkg.com/@googlemaps/markerwithlabel@1.0.3/dist/index.min.js" defer={true} />
              </>
            )}
          </ApolloProvider>
        </StyleSheetManager>
      </UserSessionProvider>
    </ErrorHandlerAlert>
  );
};

// Fetch feature flag at the app level so it applies to all routes
App.getInitialProps = async (appContext: AppContext) => {
  // Call page's getInitialProps if it exists
  const appProps = await NextApp.getInitialProps(appContext);

  // Fetch session timeout feature flag server-side
  const featureFlagSessionTimeout = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_SESSION_TIMEOUT_MIGRATION, {
    target: TARGETS.APP.GROUPS_RENDERER,
    enableStream: true,
  });

  // Get session timeout from runtime env var (only available server-side)
  const sessionTimeoutMs = process.env.NEXT_PUBLIC_SESSION_TIMEOUT_MS
    ? parseInt(process.env.NEXT_PUBLIC_SESSION_TIMEOUT_MS, 10) * 1000
    : 0;

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      featureFlagSessionTimeout,
      sessionTimeoutMs,
    },
  };
};

export default App;
